import {CouponErrorCode, getEventId} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {Tag} from '@wix/wix-ui-icons-common'
import {useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useEffect, useRef, useState} from 'react'
import {ButtonPriority, StatesButtonStates} from 'wix-ui-tpa'
import {
  HoverStyles,
  StatesButton,
  Text,
  TextButton,
  TextButtonPriority,
  TextField,
  ThreeDotsLoader,
} from 'wix-ui-tpa/cssVars'
import {FormStep} from '../../../constants'
import {useAppActions, useAppSelector} from '../../../hooks/runtime'
import {getEvent} from '../../../selectors/event'
import {
  couponSubmitted,
  getCouponCode,
  getCouponError,
  isValidatingCoupon,
  isValidatingGiftCard,
} from '../../../selectors/invoice'
import {getReservationId} from '../../../selectors/reservation'
import {classes} from './coupon-input.st.css'
import {CouponFormState, CouponInputProps} from './interfaces'

export const CouponInput = ({mobile, editStep, currentStep}: CouponInputProps) => {
  const {applyCoupon, removeCoupon} = useAppActions()
  const {t} = useTranslation()
  const [state, setState] = useState(CouponFormState.INITIAL)
  const [couponCode, setCouponCode] = useState('')
  const iconRef = useRef(null)

  const eventId = getEventId(useAppSelector(getEvent))
  const reservationId = useAppSelector(getReservationId)
  const validatingGiftCard = useAppSelector(isValidatingGiftCard)
  const validating = useAppSelector(isValidatingCoupon)
  const submitted = useAppSelector(couponSubmitted)
  const error = useAppSelector(getCouponError)
  const appliedCouponCode = useAppSelector(getCouponCode)

  const paymentStep = currentStep === FormStep.Payment
  const submitButtonState = validating
    ? StatesButtonStates.IN_PROGRESS
    : submitted
    ? StatesButtonStates.SUCCESS
    : StatesButtonStates.IDLE
  const errorMessage = !error
    ? null
    : CouponErrorCode.ERROR_COUPON_DOES_NOT_EXIST
    ? t('summary.error.couponDoesNotExist')
    : CouponErrorCode.ERROR_COUPON_HAS_EXPIRED
    ? t('summary.error.promotionEnded')
    : CouponErrorCode.ERROR_COUPON_IS_NOT_ACTIVE_YET
    ? t('summary.error.couponNotActiveYet')
    : CouponErrorCode.ERROR_COUPON_USAGE_EXCEEDED
    ? t('summary.error.couponUsageExceeded')
    : CouponErrorCode.CUSTOM_ERROR_TOO_BUSY
    ? t('summary.error.serverBusy')
    : t('summary.error.unknown')

  useEffect(() => {
    if (appliedCouponCode !== couponCode) {
      setCouponCode(appliedCouponCode)
    }
  }, [appliedCouponCode])

  useEffect(() => {
    if (submitted && !validating) {
      setTimeout(() => {
        setState(CouponFormState.APPLIED)
      }, 1000)
    }
  }, [submitted, validating])

  useEffect(() => {
    if (state === CouponFormState.APPLIED && iconRef.current) {
      iconRef.current.focus()
    }
  }, [state])

  useEffect(() => {
    if (validatingGiftCard && !submitted) {
      setState(CouponFormState.INITIAL)
      setCouponCode('')
    }
  }, [validatingGiftCard])

  const handleSubmit = () => {
    if (validating || submitted) {
      return
    }

    if (paymentStep) {
      editStep(FormStep.BuyerDetails, false)
    }

    applyCoupon(eventId, reservationId, couponCode)
  }

  const handleClear = () => {
    if (validating || submitted) {
      return
    }

    setCouponCode('')
    removeCoupon()
  }

  const handleRemove = () => {
    if (paymentStep) {
      editStep(FormStep.BuyerDetails, false)
    }

    removeCoupon()
    setState(CouponFormState.EDIT)
    setCouponCode('')
  }

  const showEditView = () => setState(CouponFormState.EDIT)

  return (
    <div className={classNames(classes.container, {[classes.mobile]: mobile})}>
      {state === CouponFormState.INITIAL && (
        <TextButton
          className={classNames(classes.textButton, {[classes.desktopFont]: !mobile})}
          onClick={showEditView}
          prefixIcon={<Tag />}
          data-hook={DH.ADD_COUPON_BUTTON}
          priority={TextButtonPriority.link}
          hoverStyle={HoverStyles.underline}
        >
          {t('couponInput_addCouponCta')}
        </TextButton>
      )}
      {state === CouponFormState.EDIT && (
        <div aria-live="polite" className={classes.applying}>
          <TextField
            label={t('couponInput_label')}
            placeholder={t('couponInput_placeholder')}
            withClearButton
            error={!!error}
            errorMessage={errorMessage}
            onChange={e => setCouponCode(e.target.value)}
            readOnly={validating || submitted}
            value={couponCode}
            newErrorMessage
            // @ts-expect-error
            errorAppearance="BackgroundAndBorder"
            onClear={handleClear}
            className={classes.couponInput}
            aria-label={t('a11y.couponCodeInputField')}
            maxLength={20}
            data-hook={DH.COUPON_CODE_INPUT}
            clearButtonAriaLabel={t('a11y.clearCouponCodeButton')}
            color="red"
          />
          <StatesButton
            aria-label={validating || submitted ? t('a11y.couponCodeApplying') : undefined}
            state={submitButtonState}
            idleContent={t('couponInput_applyButton')}
            inProgressContent={<ThreeDotsLoader className={classes.loader} />}
            onClick={handleSubmit}
            priority={ButtonPriority.basicSecondary}
            className={classes.applyCouponButton}
            data-hook={DH.COUPON_CODE_SUBMIT}
            upgrade
            fullWidth
          />
        </div>
      )}
      {state === CouponFormState.APPLIED && (
        <div className={classes.applied} data-hook={DH.COUPON_APPLIED}>
          <div className={classes.coupon}>
            <div ref={iconRef} tabIndex={0} aria-label={t('a11y.couponCodeApplied')} role="img" className={classes.tag}>
              <Tag />
            </div>
            <Text className={classNames(classes.couponCode, {[classes.desktopFont]: !mobile})}>{couponCode}</Text>
          </div>
          <TextButton
            onClick={handleRemove}
            className={classNames(classes.textButton, {[classes.desktopFont]: !mobile})}
            data-hook={DH.COUPON_CODE_CLEAR}
            aria-label={t('a11y.clearCouponCodeButton')}
            priority={TextButtonPriority.link}
            hoverStyle={HoverStyles.underline}
          >
            {t('couponInput_removeButton')}
          </TextButton>
        </div>
      )}
    </div>
  )
}
